import {
  Skeleton,
  SkeletonCard,
  SkeletonText,
  SkeletonTitle,
} from '../SkeletonComponents';

/**
 * Skeleton for the Balances tab CONTENT ONLY
 * Used within GroupPageLayout - no header or tabs included
 * Matches: MembersSection + PaymentSuggestions
 */
export function BalancesContentSkeleton() {
  return (
    <div className="space-y-6">
      {/* Members Section - matches MembersSection structure */}
      <SkeletonCard className="p-6">
        {/* Header with title and manage link */}
        <div className="flex items-center justify-between mb-4">
          <SkeletonTitle className="w-28 h-6" />
          <div className="flex items-center gap-1">
            <SkeletonText className="w-16 h-4" />
            <Skeleton className="w-4 h-4" />
          </div>
        </div>

        {/* Member list - matches MemberBalanceList structure */}
        <div className="-mx-6">
          {[1, 2, 3, 4].map(i => (
            <div
              key={i}
              className={`py-3 flex items-center justify-between gap-3 px-6 ${
                i !== 4 ? 'border-b border-gray-100 dark:border-gray-700' : ''
              }`}
            >
              {/* Left: Member Info */}
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <Skeleton className="w-10 h-10 rounded-full shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                    <SkeletonText className="w-24 h-5" />
                    {i === 1 && <Skeleton className="w-16 h-5 rounded-full" />}
                  </div>
                </div>
              </div>
              {/* Right: Balance */}
              <div className="shrink-0 text-right">
                <SkeletonText className="w-16 h-5 mb-1" />
                <SkeletonText className="w-12 h-3" />
              </div>
            </div>
          ))}
        </div>
      </SkeletonCard>

      {/* Payment Suggestions Card - matches PaymentSuggestions structure */}
      <SkeletonCard className="p-6">
        <div className="space-y-4">
          {/* Header with icon, title, badge, and button */}
          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Skeleton className="w-5 h-5" />
                  <SkeletonTitle className="w-36 h-6" />
                  <Skeleton className="w-20 h-5 rounded-full" />
                </div>
                <SkeletonText className="w-72 h-4" />
              </div>
              <Skeleton className="w-full sm:w-40 h-9 rounded-lg" />
            </div>
          </div>

          {/* Payment suggestion items - matches debt card layout */}
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div
                key={i}
                className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-100 dark:border-orange-800"
              >
                {/* Mobile: stacked layout (hidden on sm+) */}
                <div className="flex flex-col items-center sm:hidden">
                  <div className="flex items-center gap-2">
                    <Skeleton className="w-8 h-8 rounded-full" />
                    <SkeletonText className="w-20 h-4" />
                  </div>
                  <Skeleton className="w-3 h-3 my-1" />
                  <SkeletonTitle className="w-16 h-5" />
                  <Skeleton className="w-3 h-3 my-1" />
                  <div className="flex items-center gap-2">
                    <Skeleton className="w-8 h-8 rounded-full" />
                    <SkeletonText className="w-20 h-4" />
                  </div>
                  <Skeleton className="w-full h-9 rounded-lg mt-3" />
                </div>

                {/* Desktop: horizontal layout (hidden on mobile) */}
                <div className="hidden sm:block">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <Skeleton className="w-8 h-8 rounded-full shrink-0" />
                      <SkeletonText className="w-24 h-4" />
                    </div>
                    <div className="flex items-center gap-2 px-3 shrink-0">
                      <Skeleton className="w-4 h-4" />
                      <SkeletonTitle className="w-16 h-5" />
                      <Skeleton className="w-4 h-4" />
                    </div>
                    <div className="flex items-center gap-2 min-w-0 flex-1 justify-end">
                      <SkeletonText className="w-24 h-4" />
                      <Skeleton className="w-8 h-8 rounded-full shrink-0" />
                    </div>
                  </div>
                  <div className="flex justify-end mt-3">
                    <Skeleton className="w-36 h-9 rounded-lg" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Help text */}
          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="flex items-start gap-2">
              <Skeleton className="w-4 h-4 mt-0.5 shrink-0" />
              <div className="flex-1">
                <SkeletonText className="w-32 h-4 mb-1" />
                <SkeletonText className="w-full h-3" />
              </div>
            </div>
          </div>
        </div>
      </SkeletonCard>
    </div>
  );
}

/**
 * Full page skeleton for Balances page (standalone use)
 * @deprecated Use BalancesContentSkeleton with GroupPageLayout instead
 */
export function BalancesPageSkeleton() {
  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <BalancesContentSkeleton />
    </div>
  );
}
